import { Member } from '@wix/ambassador-members-ng-api/types';
import { DateTimeFormatter } from '@wix/bookings-date-time';
import { OfferedAsType } from '@wix/bookings-uou-types';
import { useSettings } from '@wix/tpa-settings/react';
import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import sortBy from 'lodash/sortBy';
import React, { useMemo } from 'react';
import { DynamicPriceErrorType, FormError } from '../../../../../types/errors';
import { FormStatus } from '../../../../../types/form-state';
import {
  PaymentOption,
  PaymentType,
  ReservedPaymentOptionIds,
} from '../../../../../types/types';
import { getServiceSlotIdentifier, mapToArray } from '../../../../../utils';
import { getErrorByType } from '../../../../../utils/errors/errors';
import {
  getDefaultPaymentOptionId,
  getPaymentTypeFromOption,
  getSlotServicePaymentOptions,
  slotServicesRequireStoredPaymentMethod,
  sortPaymentOptions,
} from '../../../../../utils/payment';
import {
  ServiceData,
  SlotService,
  SlotServices,
} from '../../../../../utils/state/types';
import { getDefaultPaymentType } from '../../../../../utils/state/utils/utils';
import {
  getParticipantPayment,
  isOwnPaymentParticipant,
  isPlanPayment,
} from '../../../../../utils/multiParticipantFlow/participantPayment';
import {
  DefaultPaymentOptionToApply,
  defaultPaymentOptionKey,
} from '../../../Actions/setDefaultPaymentOptions/setDefaultPaymentOptions';
import { useFormActions } from '../../../Hooks/useFormActions';
import { useFormComponentContext } from '../../../Hooks/useFormComponentContext';
import {
  renderSlotDynamicPricesDropdown,
  shouldRenderDynamicPriceDropdown,
} from '../../PriceOptionDropdownContainer/PriceOptionDropdownContainer';
import { MultiPaymentSelectionContainerDataHooks } from './datahooks';
import { ServicePaymentOptions } from './ServicePaymentDropdown/ServicePaymentOptions';

export type MultiPaymentSelectionContainerProps = {
  serviceData: ServiceData;
  memberDetails?: Member;
  numberOfParticipants: number;
  dateRegionalSettingsLocale: string;
  isPricingPlanInstalled: boolean;
  status: FormStatus;
  errors: FormError[];
  isCart: boolean | undefined;
  isBookingForSomeoneElse?: boolean;
  // Multi-participant flow: render this participant's own payment selection.
  participantIndex?: number;
};

export const getMultiPaymentSelectionContainer = (
  props: MultiPaymentSelectionContainerProps,
) => {
  const paymentSelection = RenderMultiPaymentSelection(props);

  if (!paymentSelection) {
    return null;
  }

  return <>{paymentSelection}</>;
};

export const MultiPaymentSelectionContainer: React.FC<
  MultiPaymentSelectionContainerProps
> = (props) => {
  const paymentSelection = RenderMultiPaymentSelection(props);

  if (!paymentSelection) {
    return null;
  }

  return <>{paymentSelection}</>;
};

const RenderMultiPaymentSelection = ({
  serviceData,
  isPricingPlanInstalled,
  numberOfParticipants,
  dateRegionalSettingsLocale,
  memberDetails,
  status,
  errors,
  isCart,
  isBookingForSomeoneElse = false,
  participantIndex,
}: MultiPaymentSelectionContainerProps) => {
  // Participants after the first are always "someone else", so plans never apply to them.
  const isOwnPayment = isOwnPaymentParticipant(participantIndex);
  const excludePricingPlanOptions = isBookingForSomeoneElse || isOwnPayment;
  const { setDefaultPaymentOptions, onSelectedVariants, clearErrorByTypes } =
    useFormActions();
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const { isPricingPlansMigratedToEcom } = useFormComponentContext();
  const settings = useSettings();
  const dateAndTimeFormatter = useMemo(
    () => new DateTimeFormatter(dateRegionalSettingsLocale),
    [dateRegionalSettingsLocale],
  );

  const slotsList = mapToArray<SlotService>(serviceData.slotServices);
  const sortOrder = getDefaultPaymentType(
    settings,
    mapToArray<SlotService>(serviceData.slotServices)[0].service.paymentTypes,
  );
  // Derived every render so the options always reflect the latest paymentDetails
  // (e.g. discount / price updates from calculatePaymentDetails). The computation is
  // cheap; the only side effect — applying the default selection — is the idempotent
  // setDefaultPaymentOptions call below.
  const servicesPaymentOptionsMap: { [key: string]: PaymentOption[] } = {};
  const defaultPaymentOptionsToApply: {
    [key: string]: DefaultPaymentOptionToApply;
  } = {};
  // ECP-12919: booking-level, so decide once for every slot rather than per service.
  const isPlanAndBookBlocked = slotServicesRequireStoredPaymentMethod(
    serviceData.slotServices,
  );
  // In the multi-participant flow the service may offer more options than a given
  // participant can pick (plans are hidden for participants after the first). Track the
  // unfiltered count so the payment section still renders for them: otherwise a visitor
  // could assume the booker's plan covers everyone.
  let hasMultipleOfferedOptions = false;
  slotsList.forEach((service) => {
    const slotIdentifier = getServiceSlotIdentifier(service.nestedSlot);
    const buildOptions = (excludePlans: boolean) =>
      getSlotServicePaymentOptions({
        slotService: service,
        isPricingPlanInstalled,
        numberOfParticipants,
        t,
        dateAndTimeFormatter,
        dateRegionalSettingsLocale,
        experiments,
        isCart,
        excludePricingPlanOptions: excludePlans,
        isPlanAndBookBlocked,
        isPricingPlansMigratedToEcom,
      });
    servicesPaymentOptionsMap[slotIdentifier] = sortPaymentOptions(
      buildOptions(excludePricingPlanOptions),
      sortOrder,
    );
    if (participantIndex !== undefined && buildOptions(false).length > 1) {
      hasMultipleOfferedOptions = true;
    }

    const newDefaultId = getDefaultPaymentOptionId({
      servicePayment: service.service.payment,
      isPricingPlanInstalled,
      settings,
      memberships: service.memberships,
      isDynamicPricingCustomOptions: !!service.dynamicPriceInfo?.customOptions,
      hasEligiblePlans: Boolean(
        service.eligiblePlans?.length || service.purchasableBenefits?.length,
      ),
      experiments,
      paymentTypes: service.service.paymentTypes,
      purchasableBenefits: service.purchasableBenefits,
      excludePricingPlanOptions,
      isPlanAndBookBlocked,
      isPricingPlansMigratedToEcom,
    });

    const availableOptions = servicesPaymentOptionsMap[slotIdentifier];
    const isOptionAvailable = (option?: PaymentOption) =>
      !!option &&
      availableOptions.some(
        (opt) => opt.id === option.id && opt.label === option.label,
      );
    // Participants after the first have no selection of their own until one is made
    // (they must not silently inherit the booker's slot-level choice).
    const currentSelection = isOwnPayment
      ? service.participants?.[participantIndex]?.payment?.selectedPaymentOption
      : service.selectedPaymentOption;
    const isCurrentSelectionValid = isOptionAvailable(currentSelection);

    if (!isCurrentSelectionValid) {
      let newPaymentOption: PaymentOption | undefined;
      // A new participant starts with the previous participant's choice, unless that was
      // a plan (plans can't be used for someone else) — then the regular default applies.
      if (isOwnPayment) {
        const previous = getParticipantPayment(service, participantIndex - 1);
        if (
          !isPlanPayment(previous) &&
          isOptionAvailable(previous.selectedPaymentOption)
        ) {
          newPaymentOption = availableOptions.find(
            (opt) =>
              opt.id === previous.selectedPaymentOption.id &&
              opt.label === previous.selectedPaymentOption.label,
          );
        }
      }
      if (!newPaymentOption && newDefaultId) {
        if (newDefaultId === ReservedPaymentOptionIds.BuyAPricingPlan) {
          newPaymentOption = availableOptions.find(
            (option) => option.type === PaymentType.BUY_PLAN,
          );
        } else {
          newPaymentOption = availableOptions.find(
            (option) => option.id === newDefaultId,
          );
        }
      }
      if (newPaymentOption) {
        const newPaymentType = getPaymentTypeFromOption(newPaymentOption);
        defaultPaymentOptionsToApply[
          defaultPaymentOptionKey(slotIdentifier, participantIndex)
        ] = {
          selectedPaymentOption: newPaymentOption,
          selectedPaymentType: newPaymentType!,
          participantIndex,
        };
      }
    }
  });

  // Apply the default selections. `setDefaultPaymentOptions` only updates state for
  // slots whose selection actually changed, so calling it unconditionally here is a
  // no-op once defaults are in place — no effect or dependency array needed.
  setDefaultPaymentOptions(defaultPaymentOptionsToApply);

  const getServiceLabel = (service: SlotService) => {
    if (
      slotsList.filter((s) => s.service.name === service.service.name)
        .length === 1
    ) {
      return t('app.bookings-form.payment-section.service-dropdown.label', {
        serviceName: service.service.name,
      });
    } else {
      return t(
        'app.bookings-form.payment-section.service-dropdown.label-at-time',
        {
          serviceName: service.service.name,
          serviceTime: dateAndTimeFormatter.formatTime(
            service.nestedSlot.startDate,
          ),
        },
      );
    }
  };

  const firstDynamicPriceError = getErrorByType({
    errorType: DynamicPriceErrorType,
    errors,
  });

  const isServiceWithPricingPlan = (slotService: SlotService) => {
    return slotService.service.payment.offeredAs.includes(
      OfferedAsType.PRICING_PLAN,
    );
  };

  const isAllServicesHaveOnePaymentOption = () => {
    return slotsList.every((slotService) => {
      return (
        servicesPaymentOptionsMap[
          getServiceSlotIdentifier(slotService.nestedSlot)
        ].length === 1
      );
    });
  };

  const shouldRenderPaymentDropdown = (slotService: SlotService): boolean => {
    const isFree =
      (slotService.paymentDetails.isFree ||
        slotService.paymentDetails.price === 0) &&
      !isServiceWithPricingPlan(slotService);
    const hasSingleOption =
      servicesPaymentOptionsMap[
        getServiceSlotIdentifier(slotService.nestedSlot)
      ].length === 1 && !hasMultipleOfferedOptions;

    return !isFree && !hasSingleOption;
  };

  const shouldRenderSlotPayment = (slotService: SlotService) => {
    return (
      shouldRenderPaymentDropdown(slotService) ||
      shouldRenderDynamicPriceDropdown(slotService)
    );
  };
  const isAnyServiceValidToView = () => {
    return slotsList.some((s) => shouldRenderSlotPayment(s));
  };
  const sortPaymentOptionsByStartTime = (slotServices: SlotServices) => {
    return sortBy(
      Object.entries(slotServices),
      (y) => y[1].nestedSlot.startDate,
    );
  };

  if (slotsList.length === 1) {
    const slotService = slotsList[0];

    if (
      !shouldRenderPaymentDropdown(slotService) &&
      !shouldRenderDynamicPriceDropdown(slotService)
    ) {
      return null;
    }

    const dynamicPricesDropdown = renderSlotDynamicPricesDropdown(
      t,
      dateAndTimeFormatter,
      slotService,
      onSelectedVariants,
      clearErrorByTypes,
      serviceData,
      status,
      firstDynamicPriceError,
    );
    const paymentOptions = shouldRenderPaymentDropdown(slotService) && (
      <ServicePaymentOptions
        service={slotService}
        participantIndex={participantIndex}
        paymentOptions={
          servicesPaymentOptionsMap[
            getServiceSlotIdentifier(slotService.nestedSlot)
          ]
        }
        label={t('app.bookings-form.payment-options-label', {
          serviceName: getServiceLabel(slotService),
        })}
        isSingleService={true}
        key={getServiceSlotIdentifier(slotService.nestedSlot)}
      />
    );
    return (
      <div data-hook={MultiPaymentSelectionContainerDataHooks.SINGLE}>
        {dynamicPricesDropdown}
        {paymentOptions}
      </div>
    );
  }

  const forceRenderPaymentDropdowns = !isAllServicesHaveOnePaymentOption();

  if (
    !forceRenderPaymentDropdowns ||
    !isAnyServiceValidToView() ||
    !servicesPaymentOptionsMap
  ) {
    return null;
  }

  return (
    <div data-hook={MultiPaymentSelectionContainerDataHooks.MAIN}>
      {sortPaymentOptionsByStartTime(serviceData.slotServices).map(
        ([slotIdentifier, slotService]) => {
          return (
            <>
              {renderSlotDynamicPricesDropdown(
                t,
                dateAndTimeFormatter,
                slotService,
                onSelectedVariants,
                clearErrorByTypes,
                serviceData,
                status,
                firstDynamicPriceError,
              )}
              {(forceRenderPaymentDropdowns ||
                shouldRenderPaymentDropdown(slotService)) && (
                <ServicePaymentOptions
                  service={slotService}
                  paymentOptions={servicesPaymentOptionsMap[slotIdentifier]}
                  label={t('app.bookings-form.payment-options-label', {
                    serviceName: getServiceLabel(slotService),
                  })}
                  isSingleService={false}
                  key={getServiceSlotIdentifier(slotService.nestedSlot)}
                />
              )}
            </>
          );
        },
      )}
    </div>
  );
};
